import { Button } from "@allergy123/ui";
import { useEffect, useRef, useState } from "react";

/**
 * A pointer-drawn signature. `onChange` receives the PNG as base64 (no data-URL prefix) after each stroke, or null
 * when cleared. The canvas is 600 by 200 CSS pixels at device resolution, which stays well under the 256 KiB the
 * API accepts (api-v2.md section 3.5).
 */
export function SignaturePad({ label, disabled = false, onChange }: { label: string; disabled?: boolean; onChange: (png: string | null) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = ref.current!;
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = 600 * scale;
    canvas.height = 200 * scale;
    const ctx = canvas.getContext("2d")!;
    ctx.scale(scale, scale);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#15161A";
  }, []);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = ref.current!.getBoundingClientRect();
    return { x: ((e.clientX - rect.left) / rect.width) * 600, y: ((e.clientY - rect.top) / rect.height) * 200 };
  };
  const emit = () => {
    const url = ref.current!.toDataURL("image/png");
    onChange(url.slice(url.indexOf(",") + 1));
  };
  const down = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    drawing.current = true;
    ref.current!.setPointerCapture(e.pointerId);
    const ctx = ref.current!.getContext("2d")!;
    const { x, y } = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
    setEmpty(false);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = ref.current!.getContext("2d")!;
    const { x, y } = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    emit();
  };
  const clear = () => {
    const canvas = ref.current!;
    const ctx = canvas.getContext("2d")!;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setEmpty(true);
    onChange(null);
  };

  return (
    <div className="flex flex-col gap-2">
      <canvas
        ref={ref}
        role="img"
        aria-label={`${label} signature pad${empty ? ", empty" : ""}`}
        className="h-[200px] w-full max-w-[600px] touch-none rounded-sm border border-rule-strong bg-[#FFFFFF]"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onPointerLeave={up}
      />
      <div className="flex items-center gap-3">
        <Button variant="secondary" onClick={clear} disabled={disabled || empty}>
          Clear
        </Button>
        <span className="text-small text-ink-secondary">Sign with a finger, stylus or mouse.</span>
      </div>
    </div>
  );
}
